import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import invoiceApi from '../api/invoice';
import { Box, Button, Sheet, Stack, Typography } from '@mui/joy';
import Breadcrumb from '../components/Breadcrumb';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import InvoiceTable from '../components/InvoiceTableOld';
import { calculateInvoicedTotalAmount, deutschNumberFormatter, extractNumber } from '../helper/utils';
import InvoiceTableFilter from '../components/InvoiceTableFilter';
import { IInvoice, IInvoiceFilter } from '../interface/IInvoice';
import dayjs from 'dayjs';
import InvoicePaymentModal from '../components/InvoicePaymentModal';
import { styled } from '@mui/joy/styles';
import { useToast } from '../helper/ToastProvider';

type Props = {};

const Item = styled(Sheet)(({ theme }) => ({
  ...theme.typography['body-sm'],
  textAlign: 'center',
  fontWeight: theme.fontWeight.md,
  color: theme.vars.palette.text.secondary,
  border: '1px solid',
  borderColor: theme.palette.divider,
  padding: theme.spacing(1),
  borderRadius: theme.radius.md,
}));

const Invoices = (props: Props) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<IInvoice[]>();
  const [filters, setFilters] = useState<IInvoiceFilter>({
    externalInvoices: true,
  });
  const [filteredInvoices, setFilteredInvoices] = useState<IInvoice[]>();
  const [offerIds, setOfferIds] = useState<string[] | undefined | null>();
  const [selectedInvoice, setSelectedInvoice] = useState<null | IInvoice>(null);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [totalNoInvoice, setTotalNoInvoice] = useState(0);
  const [totalNet, setTotalNet] = useState<string>();
  const [totalGross, setTotalGross] = useState<string>();
  const { showToast } = useToast();

  useEffect(() => {
    _getAllInvoices();
  }, []);

  const _getAllInvoices = async () => {
    try {
      const { data } = await invoiceApi.getAllInvoices();
      let uniqueArray = [...new Set(data.map((inv: IInvoice) => inv.offerId)!)];
      setOfferIds(uniqueArray.sort() as string[]);
      let temp = [...data];
      temp.sort((a, b) => extractNumber(b.invoiceId) - extractNumber(a.invoiceId));
      setInvoices(temp as any);
      applyFilters(filters, temp);
    } catch (e) {
      showToast({
        message: 'Invoice Fetch Failed',
        severity: 'danger',
      });
    }
  };

  const downloadXml = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadXmlFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Xml:', error);
    }
  };

  const downloadExcel = async (invoiceId: string) => {
    try {
      const response = await invoiceApi.downloadExcelFile(invoiceId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition.split(';')[1].split('=')[1].replace(/"/g, '');
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  const onFilterChange = (filter: keyof IInvoiceFilter, value: any) => {
    console.log(`${filter}: ${value}`);
    const updatedFilters = {
      ...filters,
      [filter]: value === '' || value === 'all' ? undefined : value, // Remove filter if empty or 'all'
    };
    const activeFilters = Object.fromEntries(Object.entries(updatedFilters).filter(([_, v]) => v !== undefined));
    setFilters(activeFilters);
    applyFilters(activeFilters, invoices!);
  };

  const applyFilters = (activeFilters: IInvoiceFilter, _invoices: IInvoice[]) => {
    let tempInvoices = [..._invoices!];
    for (let [filter, value] of Object.entries(activeFilters)) {
      if (!value) continue;
      switch (filter) {
        case 'offerId':
          tempInvoices = tempInvoices.filter(inv => inv.offerId === value);
          break;
        case 'status':
          tempInvoices = tempInvoices.filter(inv => inv.status === value);
          break;
        case 'invoiceMonth':
          tempInvoices = tempInvoices.filter(inv => {
            const invoiceMonth = dayjs(inv.invoiceDate).format('YYYY-MM');
            return invoiceMonth === value;
          });
          break;
        case 'paymentMonth':
          tempInvoices = tempInvoices.filter(inv => {
            const paymentDate = dayjs(inv.dateOfPayment);
            return paymentDate.isSame(dayjs(value));
          });
          break;
        case 'externalInvoices':
          tempInvoices = tempInvoices.filter(inv => {
            const invoiceIdParts = inv.invoiceId.split('-');
            return invoiceIdParts.length >= 2;
          });
          break;
        case 'search':
          tempInvoices = tempInvoices.filter(
            inv => inv.offer?.title?.toLowerCase().includes(value.toLowerCase()) || inv.invoiceId?.toLowerCase().includes(value.toLowerCase())
          );
          break;
        default:
          break;
      }
    }
    setFilteredInvoices(tempInvoices);
    calculateTotals(tempInvoices!);
  };

  const calculateTotals = (invoicesFiltered: IInvoice[]) => {
    setTotalNoInvoice(invoicesFiltered.length);
    const _totalNet = invoicesFiltered.reduce((sum, row) => {
      const totalAmount = calculateInvoicedTotalAmount(row.invoiceDetails, row.offer?.DEV ?? 0, row.offer?.PM ?? 0, row.offer?.discount ?? 0);
      return sum + totalAmount;
    }, 0);
    setTotalNet(deutschNumberFormatter(_totalNet));
    const _totalGross = invoicesFiltered.reduce((sum, row) => {
      const totalAmount = calculateInvoicedTotalAmount(row.invoiceDetails, row.offer?.DEV ?? 0, row.offer?.PM ?? 0, row.offer?.discount ?? 0) * 1.19;
      return sum + totalAmount;
    }, 0);
    setTotalGross(deutschNumberFormatter(_totalGross));
  };

  const onPayment = (selInvoice: IInvoice) => {
    setSelectedInvoice(selInvoice);
    setOpenPaymentModal(true);
  };

  const closePaymentModal = () => {
    setSelectedInvoice(null);
    setOpenPaymentModal(false);
  };

  const onPaymentSubmit = async (invoiceId: string, dateOfPayment: string) => {
    try {
      const res = await invoiceApi.addPaymentDate(invoiceId, dateOfPayment);
      _getAllInvoices();
      setSubmittingPayment(true);
      closePaymentModal();
      showToast({
        message: 'Payment Add Success',
        severity: 'success',
      });
    } catch (e) {
      showToast({
        message: 'Payment Add Failed',
        severity: 'danger',
      });
    } finally {
      setSubmittingPayment(false);
    }
  };

  // const test = (invoice: IInvoice) => {
  //   const invoicePositions: any[] = [];

  //   invoice.invoiceDetails.forEach((item: any) => {
  //     const existing = invoicePositions.find((r) => r.position === item.offerPosition);

  //     if (existing) {
  //       existing.timeInvoiced += item.timeInvoiced;
  //     } else {
  //       invoicePositions.push({
  //         position: item.offerPosition,
  //         rate: item.rate,
  //         subject: item.epicLink,
  //         timeInvoiced: item.timeInvoiced,
  //       });
  //     }
  //   });

  //   invoicePositions.forEach((item: any) => {
  //     item.listPrice = calculateListPrice(item.timeInvoiced / 3600, invoice.offer?.[item.rate! as keyof IOffer] as number);
  //     item.projectPrice = calculateProjectPrice(
  //       item.timeInvoiced / 3600,
  //       invoice.offer?.[item.rate! as keyof IOffer] as number,
  //       invoice.offer?.discount
  //     );
  //   });
  //   return invoicePositions.sort((a, b) => a.position - b.position);
  // };

  return (
    <>
      <Breadcrumb crumb={['Invoices']} />
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level='h2' component='h1'>
          Invoices
        </Typography>
        <Button color='primary' onClick={() => navigate('newinvoice')} startDecorator={<PersonAddRoundedIcon />} size='sm'>
          Create New
        </Button>
      </Box>
      {filteredInvoices && (
        <>
          <InvoiceTableFilter filters={filters} offerIds={offerIds!} onFilterChange={onFilterChange} />
          <Box sx={{ width: '100%' }}>
            <Stack spacing={2} direction={'row'} sx={{ alignItems: 'center' }}>
              <Item>Invoices: {totalNoInvoice}</Item>
              <Item>Total Net: {totalNet}</Item>
              <Item>Total Gross: {totalGross}</Item>
            </Stack>
          </Box>
          <InvoiceTable
            invoices={filteredInvoices}
            onDownloadXml={downloadXml}
            onDownloadExcel={downloadExcel}
            onEmailSend={_getAllInvoices}
            onPayment={onPayment}
          />
          {/* <PDFViewer height='500' showToolbar={false} width='100%'>
            <InvoicePdf
              invoice={filteredInvoices[0]}
              offer={filteredInvoices[0].offer!}
              invoicePositions={test(filteredInvoices[0])}
              client={filteredInvoices[0].offer?.client}
            />
          </PDFViewer> */}
          {/* <OrderList /> */}
        </>
      )}
      <InvoicePaymentModal
        open={openPaymentModal}
        onClose={closePaymentModal}
        onSubmit={onPaymentSubmit}
        submitting={submittingPayment}
        invoice={selectedInvoice}
      />
    </>
  );
};

export default Invoices;
